import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { formatFullNumericDateUTC } from '@/utils/shared/dateUtils';
import { 
  useReceivedIncomeDetails, 
  useFutureIncomeDetails, 
  useExpensesDetails 
} from '@/hooks/admin/AdminDashboardTab/useDashboardDetails';

type MetricCardSummaryProps = {
  receivedIncome: number;
  futureIncome: number;
  totalExpenses: number;
  isLoading?: boolean;
  formatCurrency: (value: number) => string;
  year: number;
  month: number;
  propertyIds?: string[];
  organizationIds?: string[];
};

const MetricCardSummary = ({
  receivedIncome,
  futureIncome,
  totalExpenses,
  formatCurrency,
  isLoading = false,
  year,
  month,
  propertyIds,
  organizationIds,
}: MetricCardSummaryProps) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  
  // Lazy load details only when sections are expanded
  const { data: receivedDetails, isLoading: isLoadingReceived } = useReceivedIncomeDetails({
    year,
    month,
    propertyIds,
    organizationIds,
    enabled: expandedSection === 'received',
  });

  const { data: futureDetails, isLoading: isLoadingFuture } = useFutureIncomeDetails({
    year,
    month,
    propertyIds,
    organizationIds,
    enabled: expandedSection === 'future',
  });

  const { data: expenseDetails, isLoading: isLoadingExpenses } = useExpensesDetails({
    year,
    month,
    propertyIds,
    organizationIds,
    enabled: expandedSection === 'expenses',
  });
  
  const total = receivedIncome + futureIncome - totalExpenses;
  const totalColor = total >= 0 ? 'text-green-600' : 'text-red-600';

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const formatDateRange = (checkIn: string, checkOut: string) => {
    return `${formatFullNumericDateUTC(checkIn)} - ${formatFullNumericDateUTC(checkOut)}`;
  };

  const getUniqueIncomeTypes = (incomeRecords: Array<{ income_type: string }>) => {
    const uniqueTypes = [...new Set(incomeRecords.map(r => r.income_type))];
    return uniqueTypes.join(', ');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resumen del Mes</CardTitle>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {/* Ingresos Recibidos */}
          <div className="border-b pb-3">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('received')}>
              <span className="text-sm lg:text-base font-medium">Ingresos Recibidos:</span>
              <div className="flex items-center gap-2">
                {isLoading ? (
                  <Skeleton className="h-6 w-[90px]" />
                ) : (
                  <span className="text-lg font-bold text-green-600">
                    {formatCurrency(receivedIncome)}
                  </span>
                )}
                {receivedIncome > 0 ? (
                  expandedSection === 'received' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                ) : null}
              </div>
            </div>
            
            {expandedSection === 'received' && (
              <div className="mt-3 space-y-2">
                {isLoadingReceived ? (
                  <div className="space-y-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : receivedDetails && receivedDetails.length > 0 ? (
                  receivedDetails.map((detail) => (
                    <div key={detail.booking_id} className="flex justify-between items-start text-sm bg-muted/50 p-2 rounded">
                      <div className="flex-1">
                        <div className="font-medium">
                          {detail.booking_id} - {detail.guest_name}
                        </div>
                        <div className="text-muted-foreground text-xs space-y-0.5">
                          {detail.origin_name && <div>{detail.origin_name}</div>}
                          <div>{detail.property_short_name}</div>
                          <div>{formatDateRange(detail.check_in, detail.check_out)}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-green-600">{formatCurrency(detail.total_payout)}</div>
                        <div className="text-muted-foreground text-xs">
                          {getUniqueIncomeTypes(detail.income_records)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-muted-foreground p-2">No hay ingresos recibidos en este período</div>
                )}
              </div>
            )}
          </div>

          {/* Ingresos por Recibir */}
          <div className="border-b pb-3">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('future')}>
              <span className="text-sm lg:text-base font-medium">Ingresos por Recibir:</span>
              <div className="flex items-center gap-2">
                {isLoading ? (
                  <Skeleton className="h-6 w-[90px]" />
                ) : (
                  <span className="text-lg font-bold text-blue-600">
                    {formatCurrency(futureIncome)}
                  </span>
                )}
                {futureIncome > 0 ? (
                  expandedSection === 'future' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                ) : null}
              </div>
            </div>

            {expandedSection === 'future' && (
              <div className="mt-3 space-y-2">
                {isLoadingFuture ? (
                  <div className="space-y-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : futureDetails && futureDetails.length > 0 ? (
                  futureDetails.map((detail) => (
                    <div key={detail.booking_id} className="bg-muted/50 p-2 rounded">
                      <div className="flex justify-between items-start text-sm">
                        <div className="flex-1">
                          <div className="font-medium">
                            {detail.booking_id} - {detail.guest_name}
                          </div>
                          <div className="text-muted-foreground text-xs space-y-0.5">
                            {detail.origin_name && <div>{detail.origin_name}</div>}
                            <div>{detail.property_short_name}</div>
                            <div>{formatDateRange(detail.check_in, detail.check_out)}</div>
                          </div>
                          {detail.has_partial_payment && (
                            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                              <div>Estimado: {formatCurrency(detail.estimated_payout)}</div>
                              <div>Anticipo: {formatCurrency(detail.total_income_received)}</div>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-blue-600">{formatCurrency(detail.pending_amount)}</div>
                          {detail.has_partial_payment && (
                            <div className="text-xs text-muted-foreground">Pendiente</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-muted-foreground p-2">No hay ingresos pendientes en este período</div>
                )}
              </div>
            )}
          </div>

          {/* Gastos */}
          <div className="border-b pb-3">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('expenses')}>
              <span className="text-sm lg:text-base font-medium">Gastos:</span>
              <div className="flex items-center gap-2">
                {isLoading ? (
                  <Skeleton className="h-6 w-[90px]" />
                ) : (
                  <span className="text-lg font-bold text-red-600">
                    {formatCurrency(totalExpenses)}
                  </span>
                )}
                {expenseDetails?.length ? (
                  expandedSection === 'expenses' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                ) : null}
              </div>
            </div>
            
            {expandedSection === 'expenses' && expenseDetails && (
              <div className="mt-3 space-y-2">
                {expenseDetails.map((expense) => (
                  <div key={expense.id} className="flex justify-between items-start text-sm bg-muted/50 p-2 rounded">
                    <div className="flex-1">
                      <div className="font-medium">{expense.description || 'Gasto sin descripción'}</div>
                      <div className="text-muted-foreground text-xs space-y-0.5">
                        {expense.property_short_name && <div>{expense.property_short_name}</div>}
                        <div>{formatFullNumericDateUTC(expense.expense_date)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-red-600">{formatCurrency(expense.amount)}</div>
                      <div className="text-muted-foreground text-xs">{expense.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Total */}
          <div className="pt-2">
            <div className="flex items-center justify-between">
              <span className="text-base lg:text-lg font-bold">Total:</span>
              {isLoading ? (
                <Skeleton className="h-8 w-[120px]" />
              ) : (
                <span className={`text-xl lg:text-2xl font-bold ${totalColor}`}>
                  {formatCurrency(total)}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MetricCardSummary;
